import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../lib/upload';
import { nativePhoto } from '../lib/native';

const SHAPES = {
  wide: 'w-full h-36 rounded-xl',
  square: 'w-24 h-24 rounded-xl',
  round: 'w-24 h-24 rounded-full',
  thumb: 'w-16 h-16 rounded-lg',
};

// Reusable photo field: camera on mobile, gallery everywhere, preview + progress.
const ImageUpload = ({ value, onChange, folder = 'misc', label, hint, shape = 'wide', testId = 'image-upload', className = '' }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const cam = useRef(null);
  const gal = useRef(null);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    setPct(0);
    try {
      const url = await uploadImage(file, folder, setPct);
      onChange(url);
      toast.success('Foto subida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'No se pudo subir la foto');
    } finally {
      setBusy(false);
      setPct(0);
    }
  };

  // On the native apps use the real camera / photo library; on the web the hidden input.
  const choose = async (from) => {
    const file = await nativePhoto(from);
    if (file) return pick(file);
    (from === 'camera' ? cam : gal).current?.click();
  };

  return (
    <div className={className} data-testid={testId}>
      {label && <p className="text-xs font-semibold text-gray-600 mb-1.5">{label}</p>}
      <div className={`flex items-start gap-3 ${shape === 'wide' ? 'flex-col' : ''}`}>
        <div className={`relative overflow-hidden bg-[#F7F8FA] border border-gray-200 shrink-0 ${SHAPES[shape]}`}>
          {value ? <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
            : <span className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={26} /></span>}
          {busy && <span className="absolute inset-0 bg-black/50 text-white text-xs font-bold flex flex-col items-center justify-center gap-1"><Loader2 size={18} className="animate-spin" />{pct}%</span>}
          {value && !busy && (
            <button type="button" data-testid={`${testId}-remove`} onClick={() => onChange('')} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"><X size={13} /></button>
          )}
        </div>
        <div className={`flex gap-2 ${shape === 'wide' ? 'flex-row items-center flex-wrap' : 'flex-col'}`}>
          <button type="button" data-testid={`${testId}-camera`} disabled={busy} onClick={() => choose('camera')} className="h-9 px-3 rounded-xl bg-[#FDECEC] text-[#EE3B36] text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"><Camera size={15} /> Tomar foto</button>
          <button type="button" data-testid={`${testId}-gallery`} disabled={busy} onClick={() => choose('photos')} className="h-9 px-3 rounded-xl border border-gray-200 text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-50"><Upload size={15} /> Subir imagen</button>
          {hint && <p className="text-[11px] text-gray-400 leading-tight max-w-[220px]">{hint}</p>}
        </div>
      </div>
      <input data-testid={`${testId}-camera-input`} ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
      <input data-testid={`${testId}-file-input`} ref={gal} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
};

export default ImageUpload;

// Compact round avatar with a camera badge (mobile profile screens).
export const AvatarPicker = ({ value, onChange, folder = 'avatars', size = 64, ring = 'ring-4 ring-white/30', testId = 'avatar-picker' }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file, folder)); toast.success('Foto actualizada'); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || 'No se pudo subir la foto'); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testId} onClick={async () => { const f = await nativePhoto('photos'); if (f) return pick(f); input.current?.click(); }} className="relative shrink-0" style={{ width: size, height: size }}>
      {value ? <img src={value} alt="" className={`w-full h-full rounded-full object-cover ${ring}`} /> : <span className={`w-full h-full rounded-full bg-white/20 flex items-center justify-center ${ring}`}><Camera size={22} /></span>}
      <span className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-[#EE3B36] text-white border-2 border-white flex items-center justify-center">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
      </span>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
    </button>
  );
};
